import { supabase } from './supabase.js';

/**
 * Campañas activas en ventana de fechas para un slot (carrusel).
 * @param {string} slot
 * @param {number} [limit]
 * @returns {Promise<object[]>}
 */
/**
 * Campaña con status activo y ventana de fechas que incluye «ahora» (misma lógica que RLS público).
 * @param {{ status?: string, starts_at?: string, ends_at?: string }} ad
 * @param {Date} [now]
 */
export function isAdVisibleOnSiteNow(ad, now = new Date()) {
  if (!ad || String(ad.status || '').toLowerCase() !== 'activo') return false;
  const t = now.getTime();
  const start = new Date(ad.starts_at).getTime();
  const end = new Date(ad.ends_at).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return start <= t && end >= t;
}

export async function fetchActiveAdsBySlot(slot, limit = 12) {
  try {
    const slotKey = String(slot ?? '').trim();
    if (!slotKey) return [];
    const nowIso = new Date().toISOString();
    const { data, error } = await supabase
      .from('ads')
      .select('*')
      .eq('slot', slotKey)
      .eq('status', 'activo')
      .lte('starts_at', nowIso)
      .gte('ends_at', nowIso)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true })
      .limit(Math.min(24, Math.max(1, limit)));

    if (error) {
      console.error('[adsService] fetchActiveAdsBySlot:', error.message);
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('[adsService] fetchActiveAdsBySlot:', e?.message || e);
    return [];
  }
}

/**
 * Devuelve el primer anuncio activo del slot (compatibilidad).
 * @param {string} slot
 * @returns {Promise<object|null>}
 */
export async function fetchAdBySlot(slot) {
  const list = await fetchActiveAdsBySlot(slot, 1);
  return list.length ? list[0] : null;
}

/** Slide de hero marketplace desde fila `ads`. */
export function mapAdToHeroSlide(ad) {
  if (!ad?.id) return null;
  return { ...ad, type: 'ad' };
}

/** Slide del bloque «Destacado» en Comunidad desde fila `ads`. */
export function mapAdToComunidadHeroSlide(ad) {
  if (!ad?.id) return null;
  const mediaType = String(ad.media_type || 'image').toLowerCase();
  return {
    id: `ad-${ad.id}`,
    isPromoAd: true,
    adId: ad.id,
    title: ad.campaign_name || 'DreamVinyl',
    imagenUrl: mediaType === 'video' ? null : ad.media_url,
    mediaType,
    mediaUrl: ad.media_url,
    targetUrl: ad.target_url || 'https://dreamvinyl.es',
    altText: ad.alt_text || '',
    date: null,
    place: null,
    price: null,
  };
}

/**
 * Incrementa impresiones (RPC).
 * @param {string} adId
 * @returns {Promise<void>}
 */
export async function trackAdImpression(adId) {
  try {
    const { error } = await supabase.rpc('increment_ad_impressions', { ad_id: adId });
    if (error) console.error('[adsService] trackAdImpression:', error.message);
  } catch (e) {
    console.error('[adsService] trackAdImpression:', e?.message || e);
  }
}

/**
 * Incrementa clics (RPC).
 * @param {string} adId
 * @returns {Promise<void>}
 */
export async function trackAdClick(adId) {
  try {
    const { error } = await supabase.rpc('increment_ad_clicks', { ad_id: adId });
    if (error) console.error('[adsService] trackAdClick:', error.message);
  } catch (e) {
    console.error('[adsService] trackAdClick:', e?.message || e);
  }
}

